import {
  buildCollection,
  buildEntityCallbacks,
  buildProperties,
  buildProperty,
  EntityReference,
  type EntityStatus,
} from "@firecms/core";
import {
  buildAccountPermissions,
} from "../utils";
import { codeProperty } from "../properties/common";
import { buildUnitsPath } from "./units";
import { buildStoriesPath } from "./stories";
import {
  getContentBasePath,
  type ContentScopeId,
} from "../workspace/collectionIds";
import { buildContentImportActions } from "../contentImport/buildContentImportActions";
import {
  onPreSaveContentTimestamps,
  type ContentTimestamps,
} from "./contentTimestamps";
import { CopyAction } from "./publish";
import { QuestionDifficulty } from "./questions";

export const LevelModality = {
  LEVEL_MODALITY_QUIZ: "LEVEL_MODALITY_QUIZ",
  LEVEL_MODALITY_STORY: "LEVEL_MODALITY_STORY",
  LEVEL_MODALITY_TREASURE_HUNT: "LEVEL_MODALITY_TREASURE_HUNT",
  LEVEL_MODALITY_TOWER_CLIMB: "LEVEL_MODALITY_TOWER_CLIMB",
  LEVEL_MODALITY_MAZE: "LEVEL_MODALITY_MAZE",
  LEVEL_MODALITY_REWARD: "LEVEL_MODALITY_REWARD",
} as const;

export type LevelModality = (typeof LevelModality)[keyof typeof LevelModality];

export const LevelDifficulty = {
  LEVEL_DIFFICULTY_NORMAL: "LEVEL_DIFFICULTY_NORMAL",
  LEVEL_DIFFICULTY_HARD: "LEVEL_DIFFICULTY_HARD",
  LEVEL_DIFFICULTY_SUPER_HARD: "LEVEL_DIFFICULTY_SUPER_HARD",
} as const;

export type LevelDifficulty =
  (typeof LevelDifficulty)[keyof typeof LevelDifficulty];

export const LevelPlayFormat = {
  PLAY_FORMAT_SOLO: "PLAY_FORMAT_SOLO",
  PLAY_FORMAT_TEAM: "PLAY_FORMAT_TEAM",
} as const;

export type LevelPlayFormat =
  (typeof LevelPlayFormat)[keyof typeof LevelPlayFormat];

export const PrizeAmount = {
  PRIZE_AMOUNT_NONE: "PRIZE_AMOUNT_NONE",
  PRIZE_AMOUNT_LOW: "PRIZE_AMOUNT_LOW",
  PRIZE_AMOUNT_NORMAL: "PRIZE_AMOUNT_NORMAL",
  PRIZE_AMOUNT_HIGH: "PRIZE_AMOUNT_HIGH",
  PRIZE_AMOUNT_VERY_HIGH: "PRIZE_AMOUNT_VERY_HIGH",
} as const;

export type PrizeAmount = (typeof PrizeAmount)[keyof typeof PrizeAmount];

export type QuestionDifficultyValue =
  (typeof QuestionDifficulty)[keyof typeof QuestionDifficulty];

export type Level = ContentTimestamps & {
  code: string;
  unitRef: EntityReference;
  order: number;
  questionDifficulties?: QuestionDifficultyValue[];
  modality: LevelModality;
  playFormat?: LevelPlayFormat;
  difficulty?: LevelDifficulty;
  storyRef?: EntityReference;
  prizeAmount?: PrizeAmount;
};

const omitsPlayFormat = (modality: LevelModality | undefined) =>
  modality === LevelModality.LEVEL_MODALITY_STORY ||
  modality === LevelModality.LEVEL_MODALITY_REWARD;

const QUESTION_POOL_MODALITIES = new Set<LevelModality>([
  LevelModality.LEVEL_MODALITY_QUIZ,
  LevelModality.LEVEL_MODALITY_TREASURE_HUNT,
  LevelModality.LEVEL_MODALITY_TOWER_CLIMB,
]);

const modalityEnumValues = {
  [LevelModality.LEVEL_MODALITY_QUIZ]: "Quiz",
  [LevelModality.LEVEL_MODALITY_STORY]: "Story",
  [LevelModality.LEVEL_MODALITY_TREASURE_HUNT]: "Treasure hunt",
  [LevelModality.LEVEL_MODALITY_TOWER_CLIMB]: "Tower climb",
  [LevelModality.LEVEL_MODALITY_MAZE]: "Maze",
  [LevelModality.LEVEL_MODALITY_REWARD]: "Reward",
};

const questionDifficultyEnumValues = {
  [QuestionDifficulty.QUESTION_DIFFICULTY_EASY]: "Easy",
  [QuestionDifficulty.QUESTION_DIFFICULTY_MEDIUM]: "Medium",
  [QuestionDifficulty.QUESTION_DIFFICULTY_HARD]: "Hard",
};

const levelDifficultyEnumValues = {
  [LevelDifficulty.LEVEL_DIFFICULTY_NORMAL]: "Normal",
  [LevelDifficulty.LEVEL_DIFFICULTY_HARD]: "Hard",
  [LevelDifficulty.LEVEL_DIFFICULTY_SUPER_HARD]: "Super hard",
};

const playFormatEnumValues = {
  [LevelPlayFormat.PLAY_FORMAT_SOLO]: "Solo",
  [LevelPlayFormat.PLAY_FORMAT_TEAM]: "Team",
};

const prizeAmountNoneEnumValues = {
  [PrizeAmount.PRIZE_AMOUNT_NONE]: "No prize",
};

const prizeAmountEnumValues = {
  ...prizeAmountNoneEnumValues,
  [PrizeAmount.PRIZE_AMOUNT_LOW]: "Low",
  [PrizeAmount.PRIZE_AMOUNT_NORMAL]: "Normal",
  [PrizeAmount.PRIZE_AMOUNT_HIGH]: "High",
  [PrizeAmount.PRIZE_AMOUNT_VERY_HIGH]: "Very high",
};

export const buildLevelsPath = (id: ContentScopeId) =>
  `${getContentBasePath(id)}/levels`;

const usesQuestionPool = (modality: LevelModality | undefined) =>
  modality !== undefined && QUESTION_POOL_MODALITIES.has(modality);

export const onPreSaveLevel = ({
  values,
  status,
}: {
  values: Partial<Level>;
  status: EntityStatus;
}) => {
  onPreSaveContentTimestamps({ values, status });
  const modality = values.modality;
  if (omitsPlayFormat(modality)) {
    delete values.playFormat;
  }
  if (modality === LevelModality.LEVEL_MODALITY_STORY) {
    delete values.questionDifficulties;
    values.prizeAmount = PrizeAmount.PRIZE_AMOUNT_NONE;
  } else if (!usesQuestionPool(modality)) {
    delete values.questionDifficulties;
  } else if (!values.questionDifficulties?.length) {
    delete values.questionDifficulties;
  }
  return values;
};

export const buildLevelsCollection = (id: ContentScopeId) =>
  buildCollection<Level>({
    name: "Levels",
    singularName: "Level",
    defaultSize: "s",
    description:
      "Question-based levels for individuals or teams. Draws from the unit question pool; rules are defined by game modality.",
    path: buildLevelsPath(id),
    id: "levels",
    icon: "Quiz",
    group: "Play",
    initialSort: ["order", "asc"],
    callbacks: buildEntityCallbacks({
      onPreSave: onPreSaveLevel,
    }),
    permissions: buildAccountPermissions(id),
    properties: buildProperties<Level>({
      code: codeProperty,
      unitRef: buildProperty({
        name: "Unit",
        validation: { required: true },
        dataType: "reference",
        path: buildUnitsPath(id),
        includeId: false,
        includeEntityLink: false,
        previewProperties: [],
      }),
      order: buildProperty({
        name: "Order",
        description: "Sort order within the unit.",
        validation: { required: true, min: 1 },
        dataType: "number",
      }),
      modality: buildProperty({
        name: "Modality",
        validation: { required: true },
        dataType: "string",
        enumValues: modalityEnumValues,
        defaultValue: LevelModality.LEVEL_MODALITY_QUIZ,
      }),
      playFormat: ({ values }) =>
        omitsPlayFormat(values?.modality)
          ? null
          : buildProperty({
            name: "Play format",
            description: "Solo or with the team.",
            validation: { required: true },
            dataType: "string",
            enumValues: playFormatEnumValues,
            defaultValue: LevelPlayFormat.PLAY_FORMAT_SOLO,
          }),
      difficulty: buildProperty({
        name: "Difficulty",
        description: "Map display difficulty (normal, hard, super hard).",
        dataType: "string",
        enumValues: levelDifficultyEnumValues,
        defaultValue: LevelDifficulty.LEVEL_DIFFICULTY_NORMAL,
      }),
      storyRef: ({ values }) =>
        values?.modality !== LevelModality.LEVEL_MODALITY_STORY
          ? null
          : buildProperty({
            name: "Story",
            description: "Required when modality is Story.",
            dataType: "reference",
            path: buildStoriesPath(id),
            validation: { required: true },
            includeId: false,
            includeEntityLink: false,
            previewProperties: [],
          }),
      prizeAmount: ({ values }) => {
        const isStory =
          values?.modality === LevelModality.LEVEL_MODALITY_STORY;
        return buildProperty({
          name: "Prize amount",
          description: isStory
            ? "Story levels have no chest."
            : "Chest coin size for this level (none, low, normal, high, very high).",
          validation: { required: true },
          dataType: "string",
          enumValues: isStory
            ? prizeAmountNoneEnumValues
            : prizeAmountEnumValues,
          defaultValue: isStory
            ? PrizeAmount.PRIZE_AMOUNT_NONE
            : undefined,
        });
      },
      questionDifficulties: ({ values }) =>
        usesQuestionPool(values?.modality)
          ? buildProperty({
              name: "Question difficulties",
              description:
                "Leave empty to include all difficulties. The app always shuffles the unit pool.",
              validation: { required: false },
              dataType: "array",
              of: buildProperty({
                name: "Difficulty",
                dataType: "string",
                enumValues: questionDifficultyEnumValues,
              }),
            })
          : null,
      createdAt: buildProperty({
        name: "Created",
        dataType: "date",
        readOnly: true,
        autoValue: "on_create",
        hideFromCollection: true,
      }),
      updatedAt: buildProperty({
        name: "Updated",
        dataType: "date",
        readOnly: true,
        autoValue: "on_update",
        hideFromCollection: true,
      }),
    }),
    Actions: (props) => [
      ...CopyAction(props),
      ...buildContentImportActions("levels")(props),
    ],
  });

export const buildRelatedLevelsCollection = (id: ContentScopeId) =>
  buildCollection<Partial<Level>>({
    name: "Levels",
    singularName: "Level",
    description:
      "Levels from the content attached to this session. To edit them, open the learning path from the account menu.",
    path: buildLevelsPath(id),
    initialSort: ["order", "asc"],
    hideFromNavigation: true,
    id: "relatedLevels",
    permissions: () => ({
      read: true,
      edit: false,
      create: false,
      delete: false,
    }),
    properties: {
      code: codeProperty,
      order: buildProperty({
        name: "Order",
        dataType: "number",
        readOnly: true,
      }),
      modality: buildProperty({
        name: "Modality",
        dataType: "string",
        enumValues: modalityEnumValues,
        readOnly: true,
      }),
      playFormat: ({ values }) =>
        omitsPlayFormat(values?.modality)
          ? null
          : buildProperty({
              name: "Play format",
              dataType: "string",
              enumValues: playFormatEnumValues,
              readOnly: true,
            }),
      difficulty: buildProperty({
        name: "Difficulty",
        dataType: "string",
        enumValues: levelDifficultyEnumValues,
        readOnly: true,
      }),
      prizeAmount: buildProperty({
        name: "Prize amount",
        dataType: "string",
        enumValues: prizeAmountEnumValues,
        readOnly: true,
      }),
    },
  });
